import { readShopifyInstallContext, SHOPIFY_INSTALL_CONTEXT_KEY } from '@/flows/shopifyInstall';
import { readReferralEntryId } from '@common/referral/referralAttribution';
import { getOnboardingNavigationBehavior } from '@/routeBehavior';

const readInviteTokenFromRedirect = (redirect: string | null) => {
    if (!redirect) {
        return '';
    }

    try {
        const redirectUrl = new URL(redirect, 'https://bustly.local');
        return redirectUrl.searchParams.get('invite_token')?.trim() || '';
    } catch {
        return '';
    }
};

const readReferralCodeFromRedirect = (redirect: string | null) => {
    if (!redirect) {
        return '';
    }

    try {
        const redirectUrl = new URL(redirect, 'https://bustly.local');
        return redirectUrl.searchParams.get('ref')?.trim() || '';
    } catch {
        return '';
    }
};

const readReferralEntryIdFromRedirect = (redirect: string | null) => {
    if (!redirect) {
        return '';
    }

    try {
        const redirectUrl = new URL(redirect, 'https://bustly.local');
        return readReferralEntryId(redirectUrl.search);
    } catch {
        return '';
    }
};

export const readOnboardingInviteToken = (search: string = '') => {
    const searchParams = new URLSearchParams(search);
    return searchParams.get('invite_token')?.trim()
        || readInviteTokenFromRedirect(searchParams.get('redirect'));
};

export const readOnboardingReferralCode = (search: string = '') => {
    const searchParams = new URLSearchParams(search);
    return searchParams.get('ref')?.trim()
        || readReferralCodeFromRedirect(searchParams.get('redirect'));
};

export const readOnboardingReferralEntryId = (search: string = '') => {
    const searchParams = new URLSearchParams(search);
    return readReferralEntryId(search)
        || readReferralEntryIdFromRedirect(searchParams.get('redirect'));
};

export const buildOnboardingRoutePreservingInvite = (
    search: string = '',
    currentPathname: string = '',
) => {
    const inviteToken = readOnboardingInviteToken(search);
    const referralCode = readOnboardingReferralCode(search);
    const referralEntryId = readOnboardingReferralEntryId(search);
    const { routePrefix } = getOnboardingNavigationBehavior(currentPathname);

    const installContext = readShopifyInstallContext(search);

    if (!inviteToken && !referralCode && !installContext) {
        return `${routePrefix}/onboarding`;
    }

    const nextSearchParams = new URLSearchParams();
    if (installContext) {
        nextSearchParams.set(SHOPIFY_INSTALL_CONTEXT_KEY, installContext);
    }
    if (inviteToken) {
        nextSearchParams.set('invite_token', inviteToken);
    }
    if (referralCode) {
        nextSearchParams.set('ref', referralCode);
    }
    if (referralEntryId) {
        nextSearchParams.set('referral_entry', referralEntryId);
    }
    return `${routePrefix}/onboarding?${nextSearchParams.toString()}`;
};

export const navigateToOnboardingRoute = (
    navigate: (route: string) => void,
    search: string,
    currentPathname: string,
    hardNavigate: (route: string) => void = (route) => window.location.assign(route),
) => {
    const { navigationMode } = getOnboardingNavigationBehavior(currentPathname);
    const onboardingRoute = buildOnboardingRoutePreservingInvite(search, currentPathname);
    if (navigationMode === 'document') {
        hardNavigate(onboardingRoute);
        return;
    }

    navigate(onboardingRoute);
};
